import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../hooks/useApi';
import { Input, Select, Textarea } from '../components/ui/Input';
import { Button } from '../components/ui';
import { COUNTRY_BY_CODE, COUNTRY_OPTIONS } from './ExportCustomers';

/*
 * Export supply arrangements (migration 080) — Matthew, 2026-10-16:
 *
 *   "capture the country, customer, customer code, agreement type - Exclusive agreement,
 *    preferred supplier or non-exclusive, current status - supplying or presenting,
 *    Brand - Bhuja or Infuzions ... multiple customers against countries ... a free text
 *    we can add any special supply conditions eg. No Palm Oil mentioned on pack allowed."
 *
 * One line per customer x country x brand. The customer is picked from the master — the code
 * and name shown are the master's, never retyped. Codes are stored; these labels are the UI's.
 */
const AGREEMENT_LABEL = {
  exclusive: 'Exclusive agreement',
  preferred: 'Preferred supplier',
  'non-exclusive': 'Non-exclusive',
};
// 'previous' (081, Matthew 19 Oct): "exporters which were supplying certain markets and are no
// longer", kept for history.
const STATUS_LABEL = { supplying: 'Supplying', presenting: 'Presenting', previous: 'Previously supplying' };
const STATUS_TONE = { supplying: 'success', presenting: 'info', previous: 'muted' };
const BRANDS = ['Bhuja', 'Infuzions'];

const CARD = { background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8, padding: 14 };
const LBL = { color: 'var(--text-muted)', fontSize: 11, marginBottom: 3 };
const TH = { textAlign: 'left', padding: '7px 10px', fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--border)' };
const TD = { padding: '7px 10px', fontSize: 12, color: 'var(--text-primary)', borderBottom: '1px solid var(--border-soft)', verticalAlign: 'top' };

const FTH = { padding: '4px 6px', borderBottom: '1px solid var(--border)', fontWeight: 400 };
const FIN = { fontSize: 11, padding: '4px 6px' };
const NO_FILTERS = { country: '', customer: '', brand: '', agreement: '', status: '', conditions: '' };

const EMPTY = { id: null, countryCode: '', customerCode: '', brand: 'Bhuja', agreementType: 'non-exclusive', status: 'supplying', supplyConditions: '' };

const countryName = (code) => COUNTRY_BY_CODE[code]?.name ?? code;

function Pill({ tone, children }) {
  return (
    <span style={tone === 'muted'
      ? { background: 'var(--border)', color: 'var(--text-muted)', border: '1px solid var(--border-soft)',
        borderRadius: 10, padding: '1px 8px', fontSize: 11, whiteSpace: 'nowrap' }
      : { background: `var(--${tone}-soft)`, color: tone === 'warn' ? 'var(--warn-text)' : `var(--${tone})`, border: `1px solid var(--${tone}-border)`,
      borderRadius: 10, padding: '1px 8px', fontSize: 11, whiteSpace: 'nowrap' }}>
      {children}
    </span>
  );
}

export default function ExportSupplyArrangements() {
  const apiFetch = useApi();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);   // a form value, or null
  const [err, setErr] = useState('');
  const [search, setSearch] = useState('');
  // Header filters (Matthew, 16 Oct: "Add filters on the top headings of the table").
  const [f, setF] = useState(NO_FILTERS);
  const setFilter = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const filtersOn = Object.values(f).some(Boolean) || search.trim() !== '';

  const { data: rows = [], isLoading, error } = useQuery({
    queryKey: ['export-supply'],
    queryFn: async () => {
      const r = await apiFetch('/export-supply');
      if (!r.ok) throw new Error(`Failed to load export supply arrangements: HTTP ${r.status}`);
      return r.json();
    },
  });

  const { data: customers = [] } = useQuery({
    queryKey: ['master-customers'],
    queryFn: async () => {
      const r = await apiFetch('/master-customers');
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    },
    staleTime: 5 * 60 * 1000,
  });

  // Plain A to Z (Matthew, 16 Oct) — Export-first grouping read as out of order.
  const customerOptions = useMemo(() => [...customers].sort((a, b) => a.name.localeCompare(b.name)), [customers]);

  /** Values a header dropdown can offer — only what is actually in the table. */
  const countriesInUse = useMemo(
    () => [...new Set(rows.map((r) => r.countryCode))].sort((a, b) => countryName(a).localeCompare(countryName(b))),
    [rows]);

  const save = useMutation({
    mutationFn: async (body) => {
      const r = await apiFetch('/export-supply', { method: 'PUT', body: JSON.stringify(body) });
      if (!r.ok) throw new Error((await r.json().catch(() => ({})))?.error || `HTTP ${r.status}`);
      return r.json();
    },
    onSuccess: () => { setErr(''); setEditing(null); qc.invalidateQueries({ queryKey: ['export-supply'] }); },
    onError: (e) => setErr(String(e?.message || e)),
  });

  const remove = useMutation({
    mutationFn: async (id) => {
      const r = await apiFetch(`/export-supply/${id}`, { method: 'DELETE' });
      if (!r.ok) throw new Error((await r.json().catch(() => ({})))?.error || `HTTP ${r.status}`);
      return r.json();
    },
    onSuccess: () => { setErr(''); qc.invalidateQueries({ queryKey: ['export-supply'] }); },
    onError: (e) => setErr(String(e?.message || e)),
  });

  /** country -> lines, countries by name — many customers sit under one country. */
  const grouped = useMemo(() => {
    const q = search.trim().toLowerCase();
    const m = new Map();
    const has = (v, needle) => !needle || String(v ?? '').toLowerCase().includes(needle.trim().toLowerCase());
    for (const r of rows) {
      if (f.country && r.countryCode !== f.country) continue;
      if (!has(`${r.customerName} ${r.customerCode}`, f.customer)) continue;
      if (f.brand && r.brand !== f.brand) continue;
      if (f.agreement && r.agreementType !== f.agreement) continue;
      if (f.status && r.status !== f.status) continue;
      if (!has(r.supplyConditions, f.conditions)) continue;
      if (q && !`${countryName(r.countryCode)} ${r.customerName} ${r.customerCode} ${r.supplyConditions ?? ''}`
        .toLowerCase().includes(q)) continue;
      const arr = m.get(r.countryCode) ?? [];
      arr.push(r);
      m.set(r.countryCode, arr);
    }
    return [...m.entries()].sort((a, b) => countryName(a[0]).localeCompare(countryName(b[0])));
  }, [rows, search, f]);

  const shown = grouped.reduce((n, [, l]) => n + l.length, 0);

  // Exports what is on screen — filters applied, one row per line, labels not codes.
  const exportExcel = async () => {
    const XLSX = await import('xlsx');
    const header = ['Country', 'Customer', 'Customer code', 'Channel', 'Brand', 'Agreement type', 'Status',
      'Special supply conditions', 'Last updated', 'Updated by'];
    const body = grouped.flatMap(([, list]) => list.map((r) => [
      countryName(r.countryCode), r.customerName, r.customerCode, r.segment ?? '', r.brand,
      AGREEMENT_LABEL[r.agreementType] ?? r.agreementType, STATUS_LABEL[r.status] ?? r.status,
      r.supplyConditions ?? '', r.updatedAt ? String(r.updatedAt).slice(0, 10) : '', r.updatedByName ?? '',
    ]));
    const ws = XLSX.utils.aoa_to_sheet([header, ...body]);
    ws['!cols'] = [18, 32, 14, 12, 11, 20, 12, 60, 12, 20].map((wch) => ({ wch }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Supply arrangements');
    XLSX.writeFile(wb, `export-supply-arrangements-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };
  const set = (k) => (e) => setEditing((f) => ({ ...f, [k]: e.target.value }));
  const canSave = editing && editing.countryCode && editing.customerCode && !save.isPending;

  return (
    <div>
      {(error || err) && (
        <div style={{ background: 'var(--danger-soft)', border: '1px solid var(--danger-border)', borderRadius: 6,
          color: 'var(--danger)', padding: '8px 12px', fontSize: 13, marginBottom: 12 }}>
          {String(err || error?.message || error)}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
        <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>
          {shown} {shown === 1 ? 'arrangement' : 'arrangements'} · {grouped.length} {grouped.length === 1 ? 'country' : 'countries'}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Country, customer or condition…" style={{ width: 220, fontSize: 12 }} />
          {filtersOn && (
            <Button onClick={() => { setF(NO_FILTERS); setSearch(''); }}>Clear filters</Button>
          )}
          <Button onClick={exportExcel} disabled={shown === 0}>Export to Excel</Button>
          <Button variant="primary" onClick={() => { setErr(''); setEditing({ ...EMPTY }); }}>+ Add arrangement</Button>
        </div>
      </div>

      {editing && (
        <div style={{ ...CARD, marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 10 }}>
            {editing.id ? 'Edit arrangement' : 'New arrangement'}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
            <div>
              <div style={LBL}>Country</div>
              <Select value={editing.countryCode} onChange={set('countryCode')}>
                <option value="">Select a country…</option>
                {COUNTRY_OPTIONS.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
              </Select>
            </div>
            <div>
              <div style={LBL}>Customer</div>
              <Select value={editing.customerCode} onChange={set('customerCode')}>
                <option value="">Select a customer…</option>
                {customerOptions.map((c) => (
                  <option key={c.customerCode} value={c.customerCode}>
                    {c.name} ({c.customerCode}){c.channel === 'Export' ? '' : ` · ${c.channel || 'no channel'}`}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <div style={LBL}>Customer code</div>
              <Input value={editing.customerCode || '—'} readOnly disabled />
            </div>
            <div>
              <div style={LBL}>Brand</div>
              <Select value={editing.brand} onChange={set('brand')}>
                {BRANDS.map((b) => <option key={b} value={b}>{b}</option>)}
              </Select>
            </div>
            <div>
              <div style={LBL}>Agreement type</div>
              <Select value={editing.agreementType} onChange={set('agreementType')}>
                {Object.entries(AGREEMENT_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </Select>
            </div>
            <div>
              <div style={LBL}>Current status</div>
              <Select value={editing.status} onChange={set('status')}>
                {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </Select>
            </div>
          </div>
          <div style={{ marginTop: 10 }}>
            <div style={LBL}>Special supply conditions</div>
            <Textarea value={editing.supplyConditions ?? ''} onChange={set('supplyConditions')} rows={3} maxLength={2000}
              placeholder="e.g. No palm oil mention allowed on pack" />
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
            Customer missing? Add it in the CRM first (Add an existing customer, or create it) — every line points at the customer master.
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <Button variant="primary" disabled={!canSave} onClick={() => save.mutate(editing)}>
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
            <Button onClick={() => { setErr(''); setEditing(null); }}>Cancel</Button>
          </div>
        </div>
      )}

      {isLoading && <div style={{ color: 'var(--text-muted)', padding: 30, textAlign: 'center' }}>Loading…</div>}

      {!isLoading && rows.length === 0 && !editing && (
        <div style={{ ...CARD, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13, padding: 30 }}>
          No export supply arrangements recorded yet. Use <strong>+ Add arrangement</strong> to capture the first one.
        </div>
      )}

      {!isLoading && rows.length > 0 && (
        <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ background: 'var(--border)' }}>
              {['Country', 'Customer', 'Brand', 'Agreement', 'Status', 'Special supply conditions', ''].map((h) =>
                <th key={h} style={TH}>{h}</th>)}
            </tr>
            <tr style={{ background: 'var(--bg-elevated)' }}>
              <th style={FTH}>
                <Select value={f.country} onChange={setFilter('country')} style={FIN}>
                  <option value="">All</option>
                  {countriesInUse.map((c) => <option key={c} value={c}>{countryName(c)}</option>)}
                </Select>
              </th>
              <th style={FTH}><Input value={f.customer} onChange={setFilter('customer')} placeholder="Filter…" style={FIN} /></th>
              <th style={FTH}>
                <Select value={f.brand} onChange={setFilter('brand')} style={FIN}>
                  <option value="">All</option>
                  {BRANDS.map((b) => <option key={b} value={b}>{b}</option>)}
                </Select>
              </th>
              <th style={FTH}>
                <Select value={f.agreement} onChange={setFilter('agreement')} style={FIN}>
                  <option value="">All</option>
                  {Object.entries(AGREEMENT_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </Select>
              </th>
              <th style={FTH}>
                <Select value={f.status} onChange={setFilter('status')} style={FIN}>
                  <option value="">All</option>
                  {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </Select>
              </th>
              <th style={FTH}><Input value={f.conditions} onChange={setFilter('conditions')} placeholder="Filter…" style={FIN} /></th>
              <th style={FTH} />
            </tr></thead>
            <tbody>
              {grouped.length === 0 && (
                <tr><td style={{ ...TD, color: 'var(--text-muted)' }} colSpan={7}>Nothing matches.</td></tr>
              )}
              {grouped.flatMap(([code, list]) => list.map((r, i) => (
                <tr key={r.id}>
                  {i === 0 && (
                    <td style={{ ...TD, fontWeight: 600 }} rowSpan={list.length}>
                      {countryName(code)}
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>
                        {list.length} {list.length === 1 ? 'line' : 'lines'}
                      </div>
                    </td>
                  )}
                  <td style={TD}>
                    <div>{r.customerName}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{r.customerCode}</div>
                  </td>
                  <td style={TD}>{r.brand}</td>
                  <td style={TD}>
                    <Pill tone={r.agreementType === 'exclusive' ? 'warn' : r.agreementType === 'preferred' ? 'info' : 'success'}>
                      {AGREEMENT_LABEL[r.agreementType] ?? r.agreementType}
                    </Pill>
                  </td>
                  <td style={TD}>
                    <Pill tone={STATUS_TONE[r.status] ?? 'info'}>{STATUS_LABEL[r.status] ?? r.status}</Pill>
                  </td>
                  <td style={{ ...TD, whiteSpace: 'pre-wrap', maxWidth: 360 }}>
                    {r.supplyConditions || <span style={{ color: 'var(--text-muted)' }}>—</span>}
                  </td>
                  <td style={{ ...TD, whiteSpace: 'nowrap', textAlign: 'right' }}>
                    <Button size="sm" variant="ghost" onClick={() => { setErr(''); setEditing({ ...EMPTY, ...r, supplyConditions: r.supplyConditions ?? '' }); }}>Edit</Button>
                    <Button size="sm" variant="ghost" disabled={remove.isPending}
                      onClick={() => {
                        if (window.confirm(`Delete the ${r.brand} arrangement for ${r.customerName} in ${countryName(code)}?`)) remove.mutate(r.id);
                      }}>Delete</Button>
                  </td>
                </tr>
              )))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
